import { useEffect, useState } from "react";
import { startMarkWorker } from "./markWorker";

// PROTOTYPE (hero shapes): the places on AVRAH's mark for `count` clients
// (markTargets.js), or null until they come, and for no count. A worker
// works them out off the page's thread: the first count takes a phone a few
// hundred milliseconds, which held back the hero's first drawing, and the
// reveal needs them only for its second breath. Each count is worked out
// once. If a count's places cannot be worked out, the error is thrown where
// the hero renders.
const known = new Map();
const asked = new Map();
const waiting = new Map();
let worker = null;

function answered({ data }) {
  const { resolve, reject } = waiting.get(data.count);
  waiting.delete(data.count);
  if (data.error) {
    reject(new Error(`Costellazione: no places on the mark for ${data.count} clients: ${data.error}`));
    return;
  }
  known.set(data.count, data.targets);
  resolve(data.targets);
}

// A worker that fails to start or dies answers no one: each count still
// waiting is refused, and the next count asked for starts a new one.
function failed(event) {
  const error = new Error(`Costellazione: the mark's worker failed: ${event.message}`);
  waiting.forEach(({ reject }) => reject(error));
  waiting.clear();
  worker = null;
}

function placesFor(count) {
  if (!asked.has(count)) {
    asked.set(count, new Promise((resolve, reject) => {
      if (!worker) {
        worker = startMarkWorker();
        worker.addEventListener("message", answered);
        worker.addEventListener("error", failed);
      }
      waiting.set(count, { resolve, reject });
      worker.postMessage(count);
    }));
  }
  return asked.get(count);
}

export default function useMarkTargets(count) {
  const [answer, setAnswer] = useState(() => (known.has(count) ? { count, targets: known.get(count) } : null));
  useEffect(() => {
    if (!count) return undefined;
    if (known.has(count)) {
      setAnswer({ count, targets: known.get(count) });
      return undefined;
    }
    let live = true;
    placesFor(count).then(
      (targets) => live && setAnswer({ count, targets }),
      (error) => live && setAnswer(() => {
        throw error;
      }),
    );
    return () => {
      live = false;
    };
  }, [count]);
  return answer && answer.count === count ? answer.targets : null;
}
